/* keyword-import.js - reading a keyword export into rows.
 *
 * This exists as its own module because the screen that used to do this did not
 * do it at all: the Import view was prototype markup that reported a hardcoded
 * count and imported nothing. The rules below are the part worth pinning down in
 * tests, so they live apart from the JSX.
 *
 * Two things about real Google Keyword Planner exports drive the design:
 *
 *   1. The header is NOT the first row. Exports open with a report-title line
 *      and a blank line, and the column names carry non-breaking spaces. So the
 *      header row is FOUND by scanning for a row that names a keyword column,
 *      never assumed to be row 0.
 *   2. When no such row exists we throw and name the columns we did see. A
 *      silent fallback to "first column, first row" is exactly the shape of
 *      failure that reports a confident, wrong number.
 *
 * Rows are deduplicated case-insensitively and blanks are counted, because
 * "2,000 in the file, 1,984 imported" is only a trustworthy statement if the
 * difference is itemised.
 */

export const KW_COLS = ['keyword', 'keywords', 'keyword (by relevance)', 'search term', 'search terms', 'query', 'term'];
export const VOL_COLS = ['avg. monthly searches', 'avg monthly searches', 'average monthly searches', 'volume', 'search volume', 'searches', 'monthly searches'];
export const CMP_COLS = ['competition', 'competition (indexed value)', 'competition (indexed)'];
export const BIDLO_COLS = ['top of page bid (low range)', 'bid low', 'low top of page bid'];
export const BIDHI_COLS = ['top of page bid (high range)', 'bid high', 'high top of page bid'];

// Exported headers carry non-breaking spaces and stray runs of whitespace; those
// must compare equal to a plain single space, or the column is simply not found.
export const norm = (s) => String(s == null ? '' : s).replace(/[\u00a0\s]+/g, ' ').trim().toLowerCase();

export const pick = (cells, names) => {
  for (let i = 0; i < cells.length; i++) if (names.includes(norm(cells[i]))) return i;
  return -1;
};

/* Volumes arrive as "1,300", "1K - 10K", "10 K". Take the first number and apply
 * any magnitude suffix; a range becomes its low end, which is the conservative
 * read. Anything unparseable is null, never 0, because "no data" and "nobody
 * searches this" are different facts and the register shows both. */
export function parseVolume(v) {
  if (v == null || v === '') return null;
  const s = String(v).replace(/[\s,\u00a0]/g, '');
  const m = s.match(/(\d+(?:\.\d+)?)\s*([km])?/i);
  if (!m) return null;
  const mult = m[2] ? (m[2].toLowerCase() === 'k' ? 1e3 : 1e6) : 1;
  const n = Math.round(parseFloat(m[1]) * mult);
  return Number.isFinite(n) ? n : null;
}

export const MAX_KEYWORD_LEN = 500; // keywords.keyword is VARCHAR(500)
const HEADER_SCAN_ROWS = 12;

/* Errors we raise ourselves are written for the user and shown verbatim.
 * Anything SheetJS throws from inside is not: see readAsGrid. */
export function userError(message) {
  const e = new Error(message);
  e.userFacing = true;
  return e;
}

/**
 * Array-of-arrays (a sheet, or a parsed CSV) -> { rows, blank, dupes, headerRow }.
 * Throws Error with a message written for the user.
 */
export function parseGrid(grid) {
  if (!Array.isArray(grid) || !grid.length) throw userError('That sheet is empty.');

  let hdr = -1;
  for (let i = 0; i < Math.min(grid.length, HEADER_SCAN_ROWS); i++) {
    if (pick(grid[i] || [], KW_COLS) !== -1) { hdr = i; break; }
  }
  if (hdr === -1) {
    const seen = (grid.find(r => r && r.some(c => norm(c))) || []).map(norm).filter(Boolean).slice(0, 8);
    throw userError(
      'No keyword column found. Looked for a column named Keyword'
      + (seen.length ? `, but the columns are: ${seen.join(', ')}.` : '.'),
    );
  }

  const head = grid[hdr];
  const ix = {
    kw: pick(head, KW_COLS), vol: pick(head, VOL_COLS), cmp: pick(head, CMP_COLS),
    bl: pick(head, BIDLO_COLS), bh: pick(head, BIDHI_COLS),
  };
  const at = (row, i) => (i === -1 ? '' : String(row[i] == null ? '' : row[i]).trim());

  const rows = [];
  const seen = new Set();
  let blank = 0;
  let dupes = 0;

  for (let i = hdr + 1; i < grid.length; i++) {
    const row = grid[i] || [];
    const kw = at(row, ix.kw).replace(/\s+/g, ' ').trim();
    if (!kw) { blank++; continue; }
    const key = kw.toLowerCase();
    if (seen.has(key)) { dupes++; continue; }
    seen.add(key);
    rows.push({
      kw: kw.slice(0, MAX_KEYWORD_LEN),
      vol: parseVolume(at(row, ix.vol)),
      cmp: at(row, ix.cmp) || '-',
      bl: at(row, ix.bl) || '-',
      bh: at(row, ix.bh) || '-',
    });
  }

  if (!rows.length) throw userError('The keyword column is there, but every row under it is empty.');
  return { rows, blank, dupes, headerRow: hdr + 1 };
}

/* SheetJS is ~450 kB and only the import screen needs it, so it is fetched on
 * first use rather than carried by every page load. */
let xlsxMod = null;
export const loadXLSX = async () => (xlsxMod || (xlsxMod = await import('xlsx')));

/* A file's extension is a claim, not a fact. Exports commonly arrive as an .xls
 * that is really HTML or tab-separated text, and SheetJS picks its parser from
 * the bytes: when the guess is wrong it dies somewhere deep with a message like
 * "Cannot read properties of undefined (reading 'utils')". That is a true
 * statement about SheetJS's internals and a useless one to the person holding
 * the file, so it is replaced with the thing that actually fixes it. Only our
 * own messages, which are written for them, pass through untouched. */
export const UNREADABLE = 'That file could not be read as a spreadsheet - its contents do not match its extension, which is common for exported reports. Open it in Excel or Sheets and save it again as .xlsx or .csv, then retry.';

export function readAsGrid(XLSX, buf) {
  let wb;
  try {
    wb = XLSX.read(buf, { type: 'array', raw: false });
  } catch {
    throw userError(UNREADABLE);
  }
  const wsName = wb && wb.SheetNames && wb.SheetNames[0];
  if (!wsName) throw userError('That file has no sheets in it.');
  try {
    return { grid: XLSX.utils.sheet_to_json(wb.Sheets[wsName], { header: 1, blankrows: false, defval: '' }), wsName };
  } catch {
    throw userError(UNREADABLE);
  }
}

/** Workbook bytes (.xlsx/.xls/.csv) -> the same shape as parseGrid, plus the sheet name. */
export async function parseSheet(buf) {
  const XLSX = await loadXLSX();
  const { grid, wsName } = readAsGrid(XLSX, buf);
  return { ...parseGrid(grid), sheet: wsName };
}
